/**
 * Voice Agents (ElevenLabs) — client API verso flusia-core (Task 16).
 *
 * Clona il pattern di src/lib/enrichment/api.ts:
 *  - authedFetch: fetch verso CORE_URL con JWT supabase in Authorization;
 *  - jsonOrThrow: estrae { error: { type, message } } dal body (shape core).
 *
 * DIVISIONE DI RESPONSABILITA' (vedi hooks.ts):
 *  - qui vivono SOLO le AZIONI con side-effect (byok, dial, budget, install, import)
 *    e le letture che il Data API NON espone (usage MV, voices proxy, health);
 *  - le liste read-only tenant-scoped (calls, hitl pending, tos, voice agents)
 *    passano da query supabase dirette negli hook (RLS in essere), non da qui.
 *
 * SECURITY: le risposte BYOK non contengono MAI la chiave raw (solo has_key).
 */
import { supabase } from '../supabase';

const CORE_URL =
  (import.meta.env.VITE_FLUSIA_CORE_URL as string | undefined) ?? 'https://core.flusia.it';

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const headers = new Headers(init.headers ?? {});
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(`${CORE_URL}${path}`, { ...init, headers });
}

/**
 * Errore delle API voice che PRESERVA il `type` restituito dal core
 * ({ error: { type, message } }). Il type (es. 'TwilioConfigError',
 * 'ProviderAuthError') permette al chiamante di mostrare un messaggio
 * localizzato e mirato invece del solo message grezzo del server.
 */
export class VoiceApiError extends Error {
  readonly type?: string;
  constructor(message: string, type?: string) {
    super(message);
    this.name = 'VoiceApiError';
    this.type = type;
  }
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let err = `http_${res.status}`;
    let type: string | undefined;
    try {
      const j = (await res.json()) as { error?: unknown };
      if (typeof j.error === 'string') {
        err = j.error;
      } else if (j.error && typeof j.error === 'object') {
        // Le route voice ritornano { error: { type, message } } — estraiamo entrambi.
        const e = j.error as { message?: unknown; type?: unknown };
        if (typeof e.type === 'string') type = e.type;
        if (typeof e.message === 'string') err = e.message;
        else if (typeof e.type === 'string') err = e.type;
        else err = JSON.stringify(j.error);
      }
    } catch {
      // body non JSON, lascia http_xxx
    }
    throw new VoiceApiError(err, type);
  }
  return res.json() as Promise<T>;
}

// ─── BYOK (GET /voice/byok, PUT /voice/byok/:key) ───────────────────────────

/** Chiavi provider scrivibili dal client (la `_previous` e' solo del cron rotation). */
export type VoiceProviderKey =
  | 'elevenlabs_api_key'
  | 'twilio_account_sid'
  | 'twilio_auth_token'
  | 'elevenlabs_webhook_secret';

/** Metadata BYOK — mai la chiave raw, solo `has_key`. */
export interface VoiceProviderKeyMeta {
  id: string;
  provider_key: VoiceProviderKey;
  has_key: boolean;
  enabled: boolean;
  notes: string | null;
  updated_at: string;
}

export async function getVoiceByok(): Promise<VoiceProviderKeyMeta[]> {
  const res = await authedFetch('/api/voice/byok', { method: 'GET' });
  const data = await jsonOrThrow<{ providers: VoiceProviderKeyMeta[] }>(res);
  return data.providers;
}

export async function putVoiceByok(providerKey: VoiceProviderKey, apiKey: string): Promise<void> {
  const res = await authedFetch(`/api/voice/byok/${providerKey}`, {
    method: 'PUT',
    body: JSON.stringify({ api_key: apiKey }),
  });
  await jsonOrThrow<{ ok: true; provider_key: string }>(res);
}

// ─── Voices (GET /voice/voices) ─────────────────────────────────────────────

export interface VoiceOption {
  voice_id: string;
  name: string;
  category: string | null;
  /** mp3 di anteprima hostato da ElevenLabs (play nel voice picker). */
  preview_url: string | null;
  /** Metadati descrittivi (gender/accent/age/use_case) per i chip del picker. */
  labels: Record<string, string> | null;
}

export async function listVoices(): Promise<VoiceOption[]> {
  const res = await authedFetch('/api/voice/voices', { method: 'GET' });
  const data = await jsonOrThrow<{ voices: VoiceOption[] }>(res);
  return data.voices;
}

// ─── Phone numbers (GET/POST import/PATCH/DELETE) ───────────────────────────

export interface VoicePhoneNumber {
  id: string;
  elevenlabs_phone_number_id: string;
  phone_number: string;
  country_code: string | null;
  label: string | null;
  inbound_agent_id: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export async function getPhoneNumbers(): Promise<VoicePhoneNumber[]> {
  const res = await authedFetch('/api/voice/phone-numbers', { method: 'GET' });
  const data = await jsonOrThrow<{ phone_numbers: VoicePhoneNumber[] }>(res);
  return data.phone_numbers;
}

export async function importPhoneNumber(input: {
  phone_number: string;
  label?: string;
  country_code?: string;
}): Promise<VoicePhoneNumber> {
  const res = await authedFetch('/api/voice/phone-numbers/import', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ phone_number: VoicePhoneNumber }>(res);
  return data.phone_number;
}

export async function patchPhoneNumber(
  id: string,
  updates: { label?: string | null; inbound_agent_id?: string | null },
): Promise<VoicePhoneNumber> {
  const res = await authedFetch(`/api/voice/phone-numbers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  const data = await jsonOrThrow<{ phone_number: VoicePhoneNumber }>(res);
  return data.phone_number;
}

export async function deletePhoneNumber(id: string): Promise<void> {
  const res = await authedFetch(`/api/voice/phone-numbers/${id}`, { method: 'DELETE' });
  await jsonOrThrow<{ ok: true }>(res);
}

/**
 * Import numero via SIP TRUNK (centralino Fase 4): collega il numero esistente
 * del centralino del cliente senza Twilio. Serve almeno inbound (per ricevere)
 * o outbound.address (per chiamare in uscita).
 */
export async function importSipTrunkNumber(input: {
  phone_number: string;
  label?: string;
  inbound?: { username?: string; password?: string; allowed_addresses?: string[] };
  outbound?: { address: string; transport?: 'udp' | 'tcp' | 'tls'; username?: string; password?: string };
}): Promise<VoicePhoneNumber> {
  const res = await authedFetch('/api/voice/phone-numbers/import-sip', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const data = await jsonOrThrow<{ phone_number: VoicePhoneNumber }>(res);
  return data.phone_number;
}

// ─── Usage + budget (GET /voice/usage, PUT /voice/budget) ───────────────────

export interface VoiceBudget {
  max_credits_per_day: number;
  max_credits_per_month: number;
  alert_threshold_pct: number;
  configured: boolean;
}

export interface VoiceUsageResponse {
  current_month: {
    used_credits: number;
    call_count: number;
    total_usd: number;
    total_duration_secs: number;
  };
  budget: VoiceBudget;
  history: Array<{
    month: string;
    used_credits: number;
    call_count: number;
    total_usd: number;
  }>;
  daily: Array<{
    day: string;
    used_credits: number;
    call_count: number;
    total_usd: number;
    total_duration_secs: number;
  }>;
}

export async function getVoiceUsage(): Promise<VoiceUsageResponse> {
  const res = await authedFetch('/api/voice/usage', { method: 'GET' });
  return jsonOrThrow<VoiceUsageResponse>(res);
}

export async function updateVoiceBudget(budget: {
  max_credits_per_day: number;
  max_credits_per_month: number;
  alert_threshold_pct: number;
}): Promise<{
  max_credits_per_day: number;
  max_credits_per_month: number;
  alert_threshold_pct: number;
}> {
  const res = await authedFetch('/api/voice/budget', {
    method: 'PUT',
    body: JSON.stringify(budget),
  });
  const data = await jsonOrThrow<{
    ok: true;
    budget: { max_credits_per_day: number; max_credits_per_month: number; alert_threshold_pct: number };
  }>(res);
  return data.budget;
}

// ─── Dial (manual / test / approve / reject / approve-batch) ────────────────

export interface DialResult {
  run_id: string;
  status: string;
}

export async function dialManual(input: {
  lead_id: string;
  template_id: string;
  dynamic_variables?: Record<string, string>;
}): Promise<DialResult> {
  const res = await authedFetch('/api/voice/dial/manual', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrThrow<DialResult>(res);
}

export async function dialTest(input: {
  template_id: string;
  test_number: string;
}): Promise<DialResult & { is_test: boolean }> {
  const res = await authedFetch('/api/voice/dial/test', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrThrow<DialResult & { is_test: boolean }>(res);
}

export async function approveDial(runId: string): Promise<{ ok: boolean; status: string }> {
  const res = await authedFetch(`/api/voice/dial/${runId}/approve`, { method: 'POST' });
  return jsonOrThrow<{ ok: boolean; status: string }>(res);
}

export async function rejectDial(
  runId: string,
  reason?: string,
): Promise<{ ok: boolean; status: string }> {
  const res = await authedFetch(`/api/voice/dial/${runId}/reject`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reject_reason: reason } : {}),
  });
  return jsonOrThrow<{ ok: boolean; status: string }>(res);
}

export interface ApproveBatchResult {
  approved_count: number;
  approved: string[];
  skipped: Array<{ run_id: string; status: string }>;
}

export async function approveDialBatch(runIds: string[]): Promise<ApproveBatchResult> {
  const res = await authedFetch('/api/voice/dial/approve-batch', {
    method: 'POST',
    body: JSON.stringify({ run_ids: runIds }),
  });
  return jsonOrThrow<ApproveBatchResult>(res);
}

// ─── Templates (POST /voice/templates/:id/install) ──────────────────────────

export async function installVoiceTemplate(
  templateId: string,
): Promise<{ agent_id: string; elevenlabs_agent_id: string }> {
  const res = await authedFetch(`/api/voice/templates/${templateId}/install`, { method: 'POST' });
  return jsonOrThrow<{ agent_id: string; elevenlabs_agent_id: string }>(res);
}

// ─── Config agente (PUT /voice/agents/:id/config) ───────────────────────────

/**
 * Config per-agente con sync ElevenLabs server-side: trasferimento a operatore
 * (system tool transfer_to_number), prompt custom, messaggio iniziale custom
 * (validato lato core dal disclaimerParser — 422 DisclaimerMissing se manca
 * la disclosure AI+registrazione). Campi tutti opzionali: si invia solo ciò
 * che si vuole modificare; transfer_phone_number null/'' disattiva il transfer.
 */
export interface VoiceTransferDestinationInput {
  phone_number: string;
  label?: string | null;
  condition?: string | null;
  transfer_type?: 'conference' | 'blind';
}

export async function saveVoiceAgentConfig(
  agentId: string,
  updates: {
    transfer_phone_number?: string | null;
    transfer_type?: 'conference' | 'blind';
    /** Rubrica multi-reparto (centralino): sostituisce i campi legacy singoli. */
    transfer_destinations?: VoiceTransferDestinationInput[] | null;
    /** Regole triage agente→agente. */
    agent_transfers?: Array<{ agent_id: string; condition: string }> | null;
    /** Orari apertura (null = sempre aperto). */
    business_hours?: { days: number[]; start: string; end: string } | null;
    system_prompt?: string;
    first_message?: string | null;
    /** Velocità LLM: 'fast' = flash-lite (latenza minima), 'balanced' = flash. */
    llm_preset?: 'fast' | 'balanced';
  },
): Promise<{ ok: boolean; synced: boolean; reason?: string }> {
  const res = await authedFetch(`/api/voice/agents/${agentId}/config`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
  return jsonOrThrow<{ ok: boolean; synced: boolean; reason?: string }>(res);
}

// ─── Esperienza: prova browser, voice library, audio chiamate ───────────────

/** Sessione di prova dal browser: signed URL temporanea + variabili di test. */
export async function getVoiceTestSession(agentId: string): Promise<{
  signed_url: string;
  dynamic_variables: Record<string, string>;
}> {
  const res = await authedFetch(`/api/voice/agents/${agentId}/test-session`, { method: 'GET' });
  return jsonOrThrow<{ signed_url: string; dynamic_variables: Record<string, string> }>(res);
}

export interface LibraryVoice {
  voice_id: string;
  public_owner_id: string;
  name: string;
  preview_url: string | null;
  gender: string | null;
  accent: string | null;
  language: string | null;
  description: string | null;
}

export async function searchVoiceLibrary(params: {
  search?: string;
  language?: string;
  gender?: string;
}): Promise<LibraryVoice[]> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.language) qs.set('language', params.language);
  if (params.gender) qs.set('gender', params.gender);
  const res = await authedFetch(`/api/voice/voices/library?${qs.toString()}`, { method: 'GET' });
  const data = await jsonOrThrow<{ voices: LibraryVoice[] }>(res);
  return data.voices;
}

export async function addLibraryVoice(input: {
  public_owner_id: string;
  voice_id: string;
  name: string;
}): Promise<{ voice_id: string }> {
  const res = await authedFetch('/api/voice/voices/library/add', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return jsonOrThrow<{ voice_id: string }>(res);
}

/**
 * Audio della chiamata come object URL (player nelle Chiamate). Il chiamante
 * DEVE revocare l'URL (URL.revokeObjectURL) quando ha finito.
 */
export async function fetchCallAudioUrl(conversationId: string): Promise<string> {
  const res = await authedFetch(`/api/voice/calls/${encodeURIComponent(conversationId)}/audio`, {
    method: 'GET',
  });
  if (!res.ok) {
    await jsonOrThrow(res); // throwa con type/message del core
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ─── Health (GET /agents/health) — per superadmin ───────────────────────────

export interface AgentsHealthResult {
  status: 'ok' | 'degraded' | 'down';
  checks: Record<string, { ok: boolean; latency_ms?: number; error?: string }>;
  capacity: {
    runs_in_flight: number;
    max_concurrent: number;
    saturation_pct: number;
    warning: boolean;
  };
  voice_metrics: {
    voice_runs_total: number;
    voice_call_duration_secs: number;
    voice_cost_credits_daily: number;
    voice_hitl_pending_current: number;
    voice_tool_invocations_total: number;
    voice_webhook_failures_total: number;
  };
  timestamp: string;
}

/**
 * NB: /api/agents/health ritorna HTTP 503 quando status='degraded'|'down', ma il
 * body resta un JSON valido con i dati diagnostici. NON usiamo jsonOrThrow (che
 * throwa su !res.ok): parsiamo il body e lo restituiamo se ha la shape attesa,
 * altrimenti (401/500 senza body health) throwiamo l'errore.
 */
export async function getAgentsHealth(): Promise<AgentsHealthResult> {
  const res = await authedFetch('/api/agents/health', { method: 'GET' });
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new Error(`http_${res.status}`);
  }
  if (body && typeof body === 'object' && typeof (body as { status?: unknown }).status === 'string') {
    return body as AgentsHealthResult;
  }
  // Body di errore ({error:{type,message}}) o inatteso.
  const e = (body as { error?: { message?: string; type?: string } })?.error;
  throw new Error(e?.message ?? e?.type ?? `http_${res.status}`);
}
